import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  ArrowLeft,
  Save,
  Upload,
  X as XIcon,
  ClipboardList,
  UploadCloud,
  Camera,
  Plus,
  Trash2,
} from "lucide-react";
import {
  listSafetyTemplates,
  createSafetyObservation,
  getSafetyObservationContractors,
  listContractorNamesByOrg,
  getAssignedProjects,
  fetchTowerListByProject,
  getLevelsByTowerId,
} from "../../../../../api";
import { showToast } from "../../../../../utils/toast";
import SafetyImageAnnotationModal from "../../SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../../SafetyCameraCaptureModal";
import SafetyResponsiveSignatureCanvas from "../../../SafetyResponsiveSignatureCanvas";
import RiskMatrixModal from "./RiskMatrixModal";
import {
  getContractorName,
  getContractorId,
  getUniqueContractorOptions,
  isArkadeNestProject,
  resolveInternalContractorOption,
  resolveInternalTeamDisplayLabel,
} from "../../../../../utils/safetyContractorHelpers";
import {
  getCurrentGPSLocation,
  calculateHaversineDistance,
  fetchSiteGeoFence,
  evaluateLocationStatus,
  formatCoordinate,
  verifySitePresence,
} from "../../../../../services/geofenceService";
import {
  decodeJwtPayload,
  resolveAccessToken,
  readStoredUserData,
  resolveSafetyUserProfile,
  normalizeRole,
  extractFirstRole,
  resolveSafetyActiveRole,
  resolveSafetyOrganizationId,
} from "../../../../../utils/safetyProfileHelpers";

const resolveUserProfile = resolveSafetyUserProfile;
const resolveActiveRole = resolveSafetyActiveRole;


const resolveMakerContext = () => {
  const profile = resolveUserProfile();

  const accesses = Array.isArray(profile?.accesses) ? profile.accesses : [];

  const storedProjectId = String(
    localStorage.getItem("ACTIVE_PROJECT_ID") ||
      localStorage.getItem("PROJECT_ID") ||
      localStorage.getItem("project_id") ||
      "",
  ).trim();

  const matchingStoredAccess = accesses.find(
    (access) => Number(access?.project_id) === Number(storedProjectId),
  );

  const activeAccess =
    matchingStoredAccess ||
    accesses.find((access) => access?.active !== false && access?.project_id) ||
    accesses.find((access) => access?.project_id) ||
    null;

  const projectId = String(
    storedProjectId || activeAccess?.project_id || profile?.project_id || "",
  ).trim();

  const projectName = String(
    localStorage.getItem("ACTIVE_PROJECT_NAME") ||
      localStorage.getItem("PROJECT_NAME") ||
      activeAccess?.project_name ||
      activeAccess?.project?.name ||
      profile?.project_name ||
      "",
  ).trim();

  const orgId = resolveSafetyOrganizationId(profile, projectId, activeAccess);

  if (orgId) {
    localStorage.setItem("ORG_ID", orgId);
    localStorage.setItem("org_id", orgId);
  }

  return {
    projectId,
    projectName,
    orgId,
    profile,
    accesses,
  };
};

const HAZARDS_OPTIONS = [
  "1. Physical Hazard",
  "2. Biological Hazard",
  "3. Chemical Hazard",
  "4. Mechanical Hazard",
  "5. Ergonomical Hazard",
  "6. Environmental Hazard",
  "7. Fire/Explosion Hazard",
  "8. Electrical Hazard",
  "9. Psychological Hazard",
  "10. Other Hazards",
];



const isCheckerUser = (profile) => {
  if (!profile || Object.keys(profile).length === 0) return true;

  const storedRole =
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ACTIVE_ROLE") ||
    localStorage.getItem("ROLE") ||
    localStorage.getItem("role") ||
    "";

  const normalized = normalizeRole(
    storedRole ||
      profile?.active_role ||
      profile?.role ||
      extractFirstRole(profile?.roles),
  );

  // If role is explicitly MAKER without any checker capability
  if (normalized === "MAKER") {
    const accesses = Array.isArray(profile?.accesses) ? profile.accesses : [];
    const hasChecker = accesses.some((a) => {
      const r = normalizeRole(a?.active_role || a?.role || a?.role_name);
      return (
        r.includes("CHECKER") ||
        r.includes("SAFETY") ||
        r.includes("OFFICER") ||
        r.includes("ADMIN")
      );
    });
    return hasChecker;
  }

  return true;
};

const isSafetyOfficerUser = (profile, projectId) => {
  const user =
    profile && Object.keys(profile).length > 0
      ? profile
      : resolveSafetyUserProfile();

  if (!user || typeof user !== "object") return false;

  // Direct boolean flags on profile / user
  if (
    user.is_safetyOfficer ||
    user.is_safety_officer ||
    user.isSafetyOfficer ||
    user.is_contractorSafetyOfficer ||
    user.is_contractor_safety_officer ||
    user.is_external_safety_officer ||
    user.is_internal_safety_officer
  ) {
    return true;
  }

  // Active role check from localStorage or profile
  const storedRole =
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ACTIVE_ROLE") ||
    localStorage.getItem("ROLE") ||
    localStorage.getItem("role") ||
    "";
  const activeRole = normalizeRole(
    storedRole ||
      user.active_role ||
      user.role ||
      extractFirstRole(user.roles),
  );
  if (activeRole.includes("SAFETY")) {
    return true;
  }

  // Project-specific access check
  const accesses = Array.isArray(user.accesses) ? user.accesses : [];
  if (projectId) {
    const matchingAccess = accesses.find(
      (a) =>
        Number(a?.project_id || a?.project?.id || a?.project) ===
        Number(projectId),
    );
    if (
      matchingAccess?.is_safetyOfficer ||
      matchingAccess?.is_safety_officer ||
      matchingAccess?.isSafetyOfficer
    ) {
      return true;
    }
    const accessRole = normalizeRole(
      matchingAccess?.active_role ||
        matchingAccess?.role ||
        matchingAccess?.role_name ||
        extractFirstRole(matchingAccess?.roles),
    );
    if (accessRole.includes("SAFETY")) {
      return true;
    }
  }

  // Any access with safety officer flag or role
  return accesses.some(
    (a) =>
      a?.is_safetyOfficer ||
      a?.is_safety_officer ||
      a?.isSafetyOfficer ||
      normalizeRole(
        a?.active_role || a?.role || a?.role_name || extractFirstRole(a?.roles),
      ).includes("SAFETY"),
  );
};

const hasActiveProjectAccess = (profile, projectId, projects = []) => {
  if (!projectId) {
    return true;
  }

  // 1. If project is in the user's fetched assigned projects list, they have access
  if (Array.isArray(projects) && projects.length > 0) {
    const found = projects.some(
      (p) => Number(getProjectId(p)) === Number(projectId),
    );
    if (found) return true;
  }

  // 2. Check profile.accesses
  const accesses = Array.isArray(profile?.accesses) ? profile.accesses : [];
  if (accesses.length === 0) return true;

  return accesses.some((access) => {
    const accessProjectId =
      access?.project_id || access?.project?.id || access?.project;
    return (
      Number(accessProjectId) === Number(projectId) && access?.active !== false
    );
  });
};

const canCreateObservation = (profile, projectId, projects = []) => {
  if (!isCheckerUser(profile)) return false;
  if (projectId && !hasActiveProjectAccess(profile, projectId, projects)) {
    return false;
  }
  return true;
};

const resolveUserContractorName = (profile, projectId) => {
  const directName = String(
    profile?.contractor_name ||
      profile?.company ||
      profile?.contractor?.name ||
      profile?.contractor?.contractor_name ||
      "",
  ).trim();

  if (directName) return directName;

  const accesses = Array.isArray(profile?.accesses) ? profile.accesses : [];
  const matchingAccess = accesses.find((access) => {
    const accessProjectId =
      access?.project_id || access?.project?.id || access?.project;

    return Number(accessProjectId) === Number(projectId);
  });

  return String(
    matchingAccess?.contractor_name ||
      matchingAccess?.company ||
      matchingAccess?.contractor?.name ||
      matchingAccess?.contractor?.contractor_name ||
      "",
  ).trim();
};

const normalizeCollection = (response, keys = []) => {
  const data = response?.data ?? response;

  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
};

const getProjectId = (project) =>
  project?.project_id || project?.id || project?.project?.id || "";

const getProjectName = (project) =>
  String(
    project?.project_name ||
      project?.name ||
      project?.project?.name ||
      "",
  ).trim();

const getTowerId = (tower) =>
  tower?.building_id || tower?.tower_id || tower?.id || "";

const getTowerName = (tower) =>
  String(
    tower?.building_name ||
      tower?.tower_name ||
      tower?.name ||
      "",
  ).trim();

const getFloorId = (floor) =>
  floor?.level_id || floor?.floor_id || floor?.id || "";

const getFloorName = (floor) =>
  String(
    floor?.level_name ||
      floor?.floor_name ||
      floor?.name ||
      floor?.title ||
      "",
  ).trim();

const formatTodayDDMMYYYY = () => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = now.getFullYear();

  return `${dd}-${mm}-${yyyy}`;
};

const ReadOnlyHeaderField = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <p className="mt-1 break-words text-sm font-semibold text-slate-800">
      {String(value || "").trim() || "—"}
    </p>
  </div>
);

const QuestionBadge = ({ number }) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
    {number}
  </span>
);

export const NumberedTextArea = ({
  name,
  value = "",
  onChange,
  placeholder,
  required,
  rows = 3,
  className = "",
  testId,
}) => {
  const textareaRef = useRef(null);

  const onValChange = (e) => {
    let val = e.target.value;
    if (val.length === 1 && (value === "" || value === undefined || value === null)) {
      val = "1. " + val;
    }
    if (onChange) {
      onChange({ target: { name, value: val } });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const val = el.value || "";

      const textBefore = val.substring(0, start);
      const textAfter = val.substring(end);

      const lines = textBefore.split("\n");
      const nextNum = lines.length + 1;
      const insertText = `\n${nextNum}. `;

      const newValue = textBefore + insertText + textAfter;

      if (onChange) {
        onChange({ target: { name, value: newValue } });
      }

      setTimeout(() => {
        if (el) {
          el.selectionStart = el.selectionEnd = start + insertText.length;
        }
      }, 0);
    }
  };

  return (
    <textarea
      ref={textareaRef}
      rows={rows}
      name={name}
      value={value}
      onChange={onValChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      required={required}
      data-testid={testId}
    />
  );
};

export default function CreateSafetyObservation({ onBack }) {
  const [orgId, setOrgId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [towers, setTowers] = useState([]);
  const [loadingTowers, setLoadingTowers] = useState(false);
  const [floors, setFloors] = useState([]);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);

  const isArkadeNest = useMemo(() => {
    if (isArkadeNestProject(projectName)) return true;
    if (projectId && Array.isArray(projects) && projects.length > 0) {
      const activeProject = projects.find(
        (project) =>
          Number(getProjectId(project)) === Number(projectId) ||
          String(getProjectId(project)) === String(projectId),
      );
      if (activeProject && isArkadeNestProject(getProjectName(activeProject))) {
        return true;
      }
    }
    const stored =
      localStorage.getItem("ACTIVE_PROJECT_NAME") ||
      localStorage.getItem("PROJECT_NAME") ||
      "";
    return isArkadeNestProject(stored);
  }, [projectName, projectId, projects]);

  const uniqueContractorOptions = useMemo(
    () =>
      getUniqueContractorOptions(contractors, {
        isArkadeNest,
        projectName,
      }),
    [contractors, isArkadeNest, projectName],
  );

  const [templateId, setTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locationVerificationStatus, setLocationVerificationStatus] = useState("");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [authorizationChecked, setAuthorizationChecked] = useState(false);

  const userCanCreateObservation = canCreateObservation(
    currentUserProfile,
    projectId,
    projects,
  );


  const loggedInContractorName = resolveUserContractorName(
    currentUserProfile,
    projectId,
  );

  const isSafetyOfficer = isSafetyOfficerUser(currentUserProfile, projectId);

  // Form Fields
  const [formData, setFormData] = useState({
    unsafe_act_condition_category: "",
    unsafe_act_condition_description: "",
    tower_id: "",
    tower_name: "",
    floor_id: "",
    floor_name: "",
    location_flat_area: "",
    location_combined: "",
    hazard_categories: [], // Array of checked options
    risk: "",
    name_of_contractor: "",
    contractor_id: "",
    target_date: "",
    corrective_action: "",
    preventive_action: "",
    ca_pa_combined: "",
  });

  const [observationPhotos, setObservationPhotos] = useState([]);
  const [annotatingPhotoIndex, setAnnotatingPhotoIndex] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [otherHazardText, setOtherHazardText] = useState("");

  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [showRiskMatrix, setShowRiskMatrix] = useState(false);
  const sigPadRef = useRef(null);

  const defaultInternalContractor = useMemo(() => {
    return resolveInternalContractorOption(uniqueContractorOptions, {
      isArkadeNest,
      projectName,
      projectId,
      orgId,
      profile: currentUserProfile,
    });
  }, [
    uniqueContractorOptions,
    isArkadeNest,
    projectName,
    projectId,
    orgId,
    currentUserProfile,
  ]);

  const internalTeamLabel = useMemo(() => {
    return resolveInternalTeamDisplayLabel({
      userType: currentUserProfile?.user_type,
      profile: currentUserProfile,
      isArkadeNest,
      projectName,
      projectId,
      orgId,
      project: Array.isArray(projects)
        ? projects.find(
            (p) =>
              String(getProjectId(p)) === String(projectId) ||
              Number(getProjectId(p)) === Number(projectId),
          )
        : null,
      projects,
    });
  }, [
    currentUserProfile,
    isArkadeNest,
    projectName,
    projectId,
    orgId,
    projects,
  ]);

  useEffect(() => {
    if (defaultInternalContractor) {
      setFormData((prev) => {
        const currentVal = prev.name_of_contractor;
        const isOtherContractor =
          currentVal &&
          currentVal !== defaultInternalContractor.contractorName &&
          uniqueContractorOptions.some(
            (opt) => opt.contractorName === currentVal,
          );
        if (isOtherContractor) {
          return prev;
        }

        return {
          ...prev,
          name_of_contractor: defaultInternalContractor.contractorName,
          contractor_id: defaultInternalContractor.contractorId
            ? String(defaultInternalContractor.contractorId)
            : prev.contractor_id,
        };
      });
    }
  }, [defaultInternalContractor, uniqueContractorOptions]);

  const handleAddPhotos = (fileList, openAnnotation = false) => {
    if (!fileList || fileList.length === 0) return;
    const startIndex = observationPhotos.length;
    const newItems = Array.from(fileList).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      file,
      annotatedFile: null,
      previewUrl: URL.createObjectURL(file),
      comment: "",
    }));
    setObservationPhotos((prev) => [...prev, ...newItems]);
    if (openAnnotation && newItems.length > 0) {
      setAnnotatingPhotoIndex(startIndex);
    }
  };

  const handleRemovePhoto = (indexToRemove) => {
    setObservationPhotos((prev) => {
      const item = prev[indexToRemove];
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((_, idx) => idx !== indexToRemove);
    });
  };

  const handleUpdatePhotoComment = (index, comment) => {
    setObservationPhotos((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], comment };
      }
      return next;
    });
  };

  const handleSaveAnnotation = (annotatedFile) => {
    if (annotatingPhotoIndex !== null && observationPhotos[annotatingPhotoIndex]) {
      setObservationPhotos((prev) => {
        const next = [...prev];
        const target = next[annotatingPhotoIndex];
        if (target?.previewUrl) {
          URL.revokeObjectURL(target.previewUrl);
        }
        next[annotatingPhotoIndex] = {
          ...target,
          annotatedFile,
          previewUrl: URL.createObjectURL(annotatedFile),
        };
        return next;
      });
    }
    setAnnotatingPhotoIndex(null);
  };

  useEffect(() => {
    return () => {
      observationPhotos.forEach((item) => {
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  useEffect(() => {
    const ctx = resolveMakerContext();

    setOrgId(ctx.orgId);
    setProjectId(ctx.projectId);
    setProjectName(ctx.projectName);
    setCurrentUserProfile(ctx.profile || {});
    setAuthorizationChecked(true);
  }, []);

  useEffect(() => {
    const userId =
      currentUserProfile?.user_id ||
      currentUserProfile?.id ||
      currentUserProfile?.pk;

    if (!userId) {
      setProjects([]);
      return;
    }

    const loadProjects = async () => {
      setLoadingProjects(true);

      try {
        const response = await getAssignedProjects(userId);
        const list = normalizeCollection(response, [
          "projects",
          "results",
          "data",
        ]);

        setProjects(list);

        if (projectId) {
          const activeProject = list.find(
            (project) =>
              Number(getProjectId(project)) === Number(projectId),
          );

          if (activeProject) {
            if (!projectName) {
              setProjectName(getProjectName(activeProject));
            }

            const dynamicallyResolvedOrgId = resolveSafetyOrganizationId(
              currentUserProfile,
              projectId,
              null,
              activeProject,
            );

            if (dynamicallyResolvedOrgId && dynamicallyResolvedOrgId !== orgId) {
              setOrgId(dynamicallyResolvedOrgId);
              localStorage.setItem("ORG_ID", dynamicallyResolvedOrgId);
              localStorage.setItem("org_id", dynamicallyResolvedOrgId);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch assigned projects", error);
        setProjects([]);
        showToast(
          error?.response?.data?.detail ||
            "Failed to fetch assigned projects.",
          "error",
        );
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [currentUserProfile]);







  useEffect(() => {
    const parts = [
      formData.tower_name,
      formData.floor_name,
      formData.location_flat_area,
    ].filter(Boolean);
    const combined = parts.join(", ");
    if (formData.location_combined !== combined) {
      setFormData((prev) => ({ ...prev, location_combined: combined }));
    }
  }, [
    formData.tower_name,
    formData.floor_name,
    formData.location_flat_area,
  ]);


  useEffect(() => {
    if (!projectId) {
      setTowers([]);
      setFloors([]);
      return;
    }

    const loadProjectDependencies = async () => {
      fetchObservationTemplate(projectId);

      setLoadingTowers(true);
      try {
        const towerResponse = await fetchTowerListByProject(projectId);
        const towerList = normalizeCollection(towerResponse, [
          "buildings",
          "results",
          "data",
        ]);
        setTowers(towerList);
      } catch (error) {
        console.error("Failed to fetch wings/buildings", error);
        setTowers([]);
        showToast(
          error?.response?.data?.detail ||
            "Failed to fetch wings/buildings.",
          "error",
        );
      } finally {
        setLoadingTowers(false);
      }

      if (projectId) {
        fetchContractors(orgId, projectId);
      } else {
        setContractors([]);
      }
    };

    loadProjectDependencies();
  }, [orgId, projectId]);

  useEffect(() => {
    if (!formData.tower_id) {
      setFloors([]);
      return;
    }

    const loadFloors = async () => {
      setLoadingFloors(true);

      try {
        const floorResponse = await getLevelsByTowerId(
          formData.tower_id,
        );
        const floorList = normalizeCollection(floorResponse, [
          "levels",
          "results",
          "data",
        ]);
        setFloors(floorList);
      } catch (error) {
        console.error("Failed to fetch floors", error);
        setFloors([]);
        showToast(
          error?.response?.data?.detail || "Failed to fetch floors.",
          "error",
        );
      } finally {
        setLoadingFloors(false);
      }
    };

    loadFloors();
  }, [formData.tower_id]);

  const fetchContractors = async (oId, pId) => {
    const targetProjectId = pId || projectId;
    const targetOrgId = oId || orgId;

    if (!targetProjectId) {
      setContractors([]);
      return;
    }

    setLoadingContractors(true);

    try {
      const combinedList = [];

      // 1. Primary: Load distinct registered contractors for project & org
      if (targetOrgId) {
        try {
          const orgContractorsRes = await listContractorNamesByOrg(
            targetOrgId,
            targetProjectId,
          );
          const rawOrgContractors = orgContractorsRes?.data;
          const listFromOrg = normalizeCollection(rawOrgContractors, [
            "contractors",
            "results",
            "data",
          ]);
          if (Array.isArray(listFromOrg) && listFromOrg.length > 0) {
            combinedList.push(...listFromOrg);
          }
        } catch (orgErr) {
          console.warn(
            "Failed to fetch contractors via listContractorNamesByOrg",
            orgErr,
          );
        }
      }

      // 2. Secondary: Load from getSafetyObservationContractors to get any additional contractor / maker records
      try {
        const params = {
          project_id: targetProjectId,
          role: "MAKER",
        };
        if (targetOrgId) {
          params.org_id = targetOrgId;
        }

        const response = await getSafetyObservationContractors(params);
        const listFromObs = normalizeCollection(response, [
          "contractors",
          "results",
          "data",
        ]);
        if (Array.isArray(listFromObs) && listFromObs.length > 0) {
          combinedList.push(...listFromObs);
        }
      } catch (obsErr) {
        console.warn(
          "Failed to fetch contractors via getSafetyObservationContractors",
          obsErr,
        );
      }

      setContractors(combinedList);
    } catch (error) {
      console.error("Failed to fetch contractors", error);
      setContractors([]);
      showToast(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to fetch contractors.",
        "error",
      );
    } finally {
      setLoadingContractors(false);
    }
  };

  useEffect(() => {
    const parts = [];
    if (formData.corrective_action)
      parts.push(`<b>Corrective Action:</b>\n${formData.corrective_action}`);
    if (formData.preventive_action)
      parts.push(`<b>Preventive Action:</b>\n${formData.preventive_action}`);
    const combined = parts.join("\n\n");
    if (formData.ca_pa_combined !== combined) {
      setFormData((prev) => ({ ...prev, ca_pa_combined: combined }));
    }
  }, [formData.corrective_action, formData.preventive_action]);

  const fetchObservationTemplate = async (pId = projectId) => {
    setLoadingTemplate(true);
    setTemplateId(null);
    setSelectedTemplate(null);
    try {
      const res = await listSafetyTemplates({
        template_type: "OBSERVATION",
        status: "ACTIVE",
        project_id: pId,
      });
      const data = res?.data;
      const list = Array.isArray(data) ? data : data?.results || [];
      if (list.length > 0) {
        setTemplateId(list[0].id);
        setSelectedTemplate(list[0]);
      } else {
        showToast(
          "No active observation template found for this project.",
          "error",
        );
      }
    } catch (err) {
      showToast("Failed to fetch observation template.", "error");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHazardToggle = (hazard) => {
    setFormData((prev) => {
      const current = [...prev.hazard_categories];
      if (current.includes(hazard)) {
        return {
          ...prev,
          hazard_categories: current.filter((h) => h !== hazard),
        };
      } else {
        return { ...prev, hazard_categories: [...current, hazard] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!projectId) {
      showToast("Please select a project.", "error");
      return;
    }

    if (!userCanCreateObservation) {
      showToast(
        "You are not authorized to create a Safety Observation for the selected project.",
        "error",
      );
      return;
    }

    if (!templateId) {
      showToast("No active observation template. Cannot submit.", "error");
      return;
    }

    if (!orgId || !projectId) {
      showToast(
        "Organization or active project context was not found.",
        "error",
      );
      return;
    }



    if (!formData.unsafe_act_condition_category) {
      showToast("Please select the unsafe act/condition category.", "error");
      return;
    }

    if (!formData.unsafe_act_condition_description.trim()) {
      showToast("Please enter the unsafe act/condition description.", "error");
      return;
    }

    if (
      !formData.tower_id ||
      !formData.floor_id ||
      !formData.location_flat_area.trim()
    ) {
      showToast("Please complete the location details.", "error");
      return;
    }

    if (formData.hazard_categories.length === 0) {
      showToast("Please select at least one Hazard/Risk category.", "error");
      return;
    }

    if (
      formData.hazard_categories.includes("10. Other Hazards") &&
      !otherHazardText.trim()
    ) {
      showToast("Please specify the other hazard.", "error");
      return;
    }

    if (!formData.risk) {
      showToast("Please select a risk level.", "error");
      return;
    }

    if (observationPhotos.length === 0) {
      showToast("Please provide a photograph of the unsafe act.", "error");
      return;
    }

    const datePart = (formData.target_date || "").split("T")[0];
    const timePart = (formData.target_date || "").split("T")[1];

    if (!datePart || !timePart) {
      showToast(
        "Please provide both date and time for the Target Date.",
        "error",
      );
      return;
    }

    const selectedDate = new Date(formData.target_date);
    const currentDate = new Date();

    if (Number.isNaN(selectedDate.getTime()) || selectedDate <= currentDate) {
      showToast("Target date and time must be in the future.", "error");
      return;
    }

    if (!formData.corrective_action.trim()) {
      showToast("Please enter the corrective action.", "error");
      return;
    }

    if (!formData.preventive_action.trim()) {
      showToast("Please enter the preventive action.", "error");
      return;
    }

    setIsSignatureModalOpen(true);
  };

  const confirmSubmit = async (signatureFile) => {
    if (!projectId) {
      showToast("Please select a project.", "error");
      return;
    }

    if (!userCanCreateObservation) {
      showToast(
        "You are not authorized to create this Safety Observation.",
        "error",
      );
      return;
    }

    if (!signatureFile) {
      showToast("Checker signature is required.", "error");
      return;
    }

    setSubmitting(true);
    let userCoords = null;

    try {
      const geoCheck = await verifySitePresence({
        projectId,
        customOptions: { timeout: 12000, enableHighAccuracy: true },
      });

      if (!geoCheck.allowed) {
        setSubmitting(false);
        setLocationVerificationStatus("");
        showToast(geoCheck.message || "Location verification failed.", "error");
        return;
      }

      if (!geoCheck.isBypassed && geoCheck.coords) {
        userCoords = geoCheck.coords;
        setLocationVerificationStatus("Location verified. Submitting observation...");
      } else {
        setLocationVerificationStatus("Submitting observation...");
      }
    } catch (geoErr) {
      setSubmitting(false);
      setLocationVerificationStatus("");
      const userMsg =
        geoErr.userMessage ||
        geoErr.message ||
        "Location access is required to raise a Safety Observation. Please enable GPS permissions and try again.";
      showToast(userMsg, "error");
      return;
    }

    try {
      const payload = new FormData();

      payload.append("active_role", "CHECKER");
      payload.append("template", String(templateId));
      payload.append("org_id", String(orgId));
      payload.append("project_id", String(projectId));

      const resolvedOrgName = String(
        localStorage.getItem("ORGANIZATION_NAME") ||
          localStorage.getItem("org_name") ||
          localStorage.getItem("active_org_name") ||
          currentUserProfile?.organization_name ||
          currentUserProfile?.org_name ||
          "",
      ).trim();
      if (resolvedOrgName) {
        payload.append("org_name", resolvedOrgName);
      }

      if (userCoords) {
        payload.append("user_latitude", String(formatCoordinate(userCoords.latitude, 7)));
        payload.append("user_longitude", String(formatCoordinate(userCoords.longitude, 7)));
        if (userCoords.accuracy !== undefined && userCoords.accuracy !== null) {
          payload.append("user_location_accuracy", String(userCoords.accuracy));
        }
      }

      if (projectName) {
        payload.append("project_name", projectName);
      }

      const templateMeta = selectedTemplate?.report_header_meta || {};
      const reportHeaderMeta = {
        format_no: templateMeta.format_no || "ADL-OH&S-F012",
        revision_no: templateMeta.revision_no || "R0",
        issued_date: templateMeta.issued_date || "01-09-2025",
        revision_date: templateMeta.revision_date || "",
        project: projectName || templateMeta.project || (projectId ? `Project ${projectId}` : ""),
        date_of_inspection: templateMeta.date_of_inspection || formatTodayDDMMYYYY(),
        inspection_report_no: templateMeta.inspection_report_no || "",
        inspection_report_prefix: templateMeta.inspection_report_prefix || "AN-ADL-SHOR",
      };
      payload.append("report_header_meta", JSON.stringify(reportHeaderMeta));

      payload.append(
        "unsafe_act_condition_category",
        formData.unsafe_act_condition_category,
      );
      payload.append(
        "unsafe_act_condition_description",
        formData.unsafe_act_condition_description.trim(),
      );

      payload.append("tower_id", String(formData.tower_id));
      payload.append("tower_name", formData.tower_name);
      payload.append("floor_id", String(formData.floor_id));
      payload.append("floor_name", formData.floor_name);
      payload.append(
        "location_extra",
        formData.location_flat_area.trim(),
      );
      payload.append(
        "location_combined",
        formData.location_combined,
      );

      const mappedHazards = formData.hazard_categories.map((hazard) =>
        hazard === "10. Other Hazards"
          ? `10. Other Hazards: ${otherHazardText.trim()}`
          : hazard,
      );

      payload.append(
        "hazard_categories",
        JSON.stringify(mappedHazards),
      );
      payload.append("risk", formData.risk);
      payload.append("target_date", formData.target_date);
      payload.append(
        "corrective_action",
        formData.corrective_action.trim(),
      );
      payload.append(
        "preventive_action",
        formData.preventive_action.trim(),
      );
      payload.append(
        "ca_pa_combined",
        formData.ca_pa_combined,
      );

      /*
       * External Checker:
       * contractor is read from USER_DATA and sent automatically.
       * The contractor dropdown is not displayed.
       *
       * Internal Checker:
       * contractor is optional. Blank means internal Maker workflow.
       * Selecting a contractor means external Maker workflow for that
       * contractor.
       */
      const contractorName = String(
        formData.name_of_contractor ||
          (defaultInternalContractor
            ? defaultInternalContractor.contractorName
            : ""),
      ).trim();

      if (contractorName) {
        payload.append("contractor_name", contractorName);
        payload.append("name_of_contractor", contractorName);

        const selectedOption = uniqueContractorOptions.find(
          (opt) =>
            opt.normalizedName === contractorName.toLowerCase() ||
            opt.contractorName === contractorName,
        );

        const contractorId =
          formData.contractor_id ||
          selectedOption?.contractorId ||
          (defaultInternalContractor?.normalizedName ===
          contractorName.toLowerCase()
            ? defaultInternalContractor?.contractorId
            : null);

        if (contractorId) {
          payload.append("contractor_id", String(contractorId));
        }
      }

      if (observationPhotos.length > 0) {
        const firstPhoto = observationPhotos[0];
        const firstFile = firstPhoto.annotatedFile || firstPhoto.file;

        payload.append(
          "photograph_of_unsafe_act",
          firstFile,
        );
        payload.append(
          "photograph_of_unsafe_act_comment",
          (firstPhoto.comment || "").trim(),
        );

        /*
         * The ViewSet stores all normalized initial photograph records
         * from these indexed multipart fields.
         */
        observationPhotos.forEach((item, index) => {
          payload.append(`initial_photographs[${index}][image]`, item.file);
          if (item.annotatedFile) {
            payload.append(
              `initial_photographs[${index}][annotated_image]`,
              item.annotatedFile,
            );
          }
          payload.append(
            `initial_photographs[${index}][comment]`,
            (item.comment || "").trim(),
          );
        });
      }

      payload.append("creator_signature", signatureFile);

      /*
       * Do not send safety_officers, maker_id or maker_name.
       * The ViewSet resolves eligible Makers from project_id, role=MAKER,
       * Safety Officer access and the optional contractor context.
       */
      await createSafetyObservation(payload);

      showToast(
        "Safety Observation Submitted Successfully!",
        "success",
      );

      setIsSignatureModalOpen(false);

      if (onBack) {
        onBack();
      }
    } catch (err) {
      const responseData = err?.response?.data;
      let message = err?.message || "Submission failed.";

      if (typeof responseData === "string") {
        message = responseData;
      } else if (responseData?.geofence) {
        message = responseData.geofence;
      } else if (responseData?.detail) {
        message = responseData.detail;
      } else if (responseData && typeof responseData === "object") {
        const firstError = Object.entries(responseData)[0];

        if (firstError) {
          const [field, value] = firstError;

          if (Array.isArray(value)) {
            message = `${field}: ${value.join(", ")}`;
          } else if (value && typeof value === "object") {
            message =
              value.message ||
              `${field}: ${JSON.stringify(value)}`;
          } else {
            message = `${field}: ${String(value)}`;
          }
        }
      }

      showToast(message, "error");
    } finally {
      setSubmitting(false);
      setLocationVerificationStatus("");
    }
  };

  if (!authorizationChecked) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-sm text-slate-500">
          Checking Safety Observation access...
        </p>
      </div>
    );
  }

  if (!isCheckerUser(currentUserProfile)) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl items-center justify-center p-4 sm:p-6">
        <div className="w-full rounded-2xl border border-red-200 bg-white p-6 sm:p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <XIcon className="h-6 w-6 text-red-600" />
          </div>

          <h2 className="text-lg sm:text-xl font-bold text-slate-900">
            Observation Creation Not Authorized
          </h2>

          <p className="mt-3 text-sm leading-6 text-slate-600">
            Only a user with the active CHECKER role or Safety Officer
            access can create a Safety Observation.
          </p>

          <button
            type="button"
            onClick={onBack}
            className="mt-6 w-full sm:w-auto rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-3 sm:p-6 bg-slate-50 min-h-screen">
      <div className="mb-6 flex items-center gap-3 sm:gap-4">
        <button
          onClick={onBack}
          className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm transition hover:bg-slate-100"
        >
          <ArrowLeft className="h-5 w-5 text-slate-700" />
        </button>
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <div className="hidden xs:flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100">
            <ClipboardList className="h-5 w-5 text-orange-500" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 leading-tight">
              Safety & Housekeeping Observation Report
            </h1>
            <p className="text-xs sm:text-sm text-slate-500">
              Fill the form below to submit your observation.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Observation Details Header (Safety Inspection Theme) */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-900">
                Observation Details
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Complete the observation details below before answering the checklist questions. Required fields marked with * must be filled and will be included in the generated report.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Row 1 */}
              <ReadOnlyHeaderField
                label="FORMAT NO."
                value={selectedTemplate?.report_header_meta?.format_no || "ADL-OH&S-F012"}
              />

              <ReadOnlyHeaderField
                label="REVISION NO."
                value={selectedTemplate?.report_header_meta?.revision_no || "R0"}
              />

              <ReadOnlyHeaderField
                label="ISSUED DATE"
                value={selectedTemplate?.report_header_meta?.issued_date || "01-09-2025"}
              />

              {/* Row 2 */}
              <ReadOnlyHeaderField
                label="REVISION DATE"
                value={selectedTemplate?.report_header_meta?.revision_date || "—"}
              />

              <ReadOnlyHeaderField
                label="PROJECT"
                value={projectName || selectedTemplate?.report_header_meta?.project || (projectId ? `Project ${projectId}` : "—")}
              />

              <ReadOnlyHeaderField
                label="INSPECTION REPORT NO."
                value={selectedTemplate?.report_header_meta?.inspection_report_no || "Auto-generated on creation"}
              />

              {/* Row 3 */}
              <ReadOnlyHeaderField
                label="DATE OF INSPECTION"
                value={selectedTemplate?.report_header_meta?.date_of_inspection || formatTodayDDMMYYYY()}
              />
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-start gap-3">
              <QuestionBadge number={1} />
              <div>
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  WHAT UNSAFE ACT / CONDITION OBSERVED{" "}
                  <span className="text-red-500">*</span>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <select
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm"
                  value={formData.unsafe_act_condition_category}
                  onChange={(e) =>
                    handleChange(
                      "unsafe_act_condition_category",
                      e.target.value,
                    )
                  }
                  required
                >
                  <option value="">Select Category...</option>
                  <option value="Unsafe Act">Unsafe Act</option>
                  <option value="Unsafe Condition">Unsafe Condition</option>
                </select>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Short answer text"
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm"
                  value={formData.unsafe_act_condition_description}
                  onChange={(e) =>
                    handleChange(
                      "unsafe_act_condition_description",
                      e.target.value,
                    )
                  }
                  required
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-start gap-3">
              <QuestionBadge number={2} />
              <div>
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  LOCATION <span className="text-red-500">*</span>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Project <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm disabled:bg-slate-100"
                  value={projectId}
                  disabled={loadingProjects}
                  onChange={(event) => {
                    const nextProjectId = event.target.value;
                    const selectedProject = projects.find(
                      (project) =>
                        Number(getProjectId(project)) ===
                        Number(nextProjectId),
                    );

                    setProjectId(nextProjectId);
                    setProjectName(getProjectName(selectedProject));
                    setTowers([]);
                    setFloors([]);
                    setFormData((prev) => ({
                      ...prev,
                      tower_id: "",
                      tower_name: "",
                      floor_id: "",
                      floor_name: "",
                      location_extra: "",
                      location_flat_area: "",
                      location_combined: "",
                      name_of_contractor: "",
                      contractor_id: "",
                    }));
                  }}
                  required
                >
                  <option value="">Select Project...</option>
                  {projects.map((project, index) => {
                    const optionId = getProjectId(project);
                    const optionName = getProjectName(project);

                    if (!optionId) return null;

                    return (
                      <option
                        key={optionId || index}
                        value={optionId}
                      >
                        {optionName || `Project ${optionId}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Wing / Building <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm disabled:bg-slate-100"
                  value={formData.tower_id}
                  disabled={!projectId || loadingTowers}
                  onChange={(event) => {
                    const towerId = event.target.value;
                    const selectedTower = towers.find(
                      (tower) =>
                        Number(getTowerId(tower)) === Number(towerId),
                    );

                    setFormData((prev) => ({
                      ...prev,
                      tower_id: towerId,
                      tower_name: getTowerName(selectedTower),
                      floor_id: "",
                      floor_name: "",
                    }));
                    setFloors([]);
                  }}
                  required
                >
                  <option value="">
                    {loadingTowers ? "Loading Wings..." : "Select Wing..."}
                  </option>
                  {towers.map((tower, index) => {
                    const towerId = getTowerId(tower);
                    const towerName = getTowerName(tower);

                    if (!towerId) return null;

                    return (
                      <option key={towerId || index} value={towerId}>
                        {towerName || `Wing ${towerId}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Floor <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm disabled:bg-slate-100"
                  value={formData.floor_id}
                  disabled={!formData.tower_id || loadingFloors}
                  onChange={(event) => {
                    const floorId = event.target.value;
                    const selectedFloor = floors.find(
                      (floor) =>
                        Number(getFloorId(floor)) === Number(floorId),
                    );

                    setFormData((prev) => ({
                      ...prev,
                      floor_id: floorId,
                      floor_name: getFloorName(selectedFloor),
                    }));
                  }}
                  required
                >
                  <option value="">
                    {loadingFloors ? "Loading Floors..." : "Select Floor..."}
                  </option>
                  {floors.map((floor, index) => {
                    const floorId = getFloorId(floor);
                    const floorName = getFloorName(floor);

                    if (!floorId) return null;

                    return (
                      <option key={floorId || index} value={floorId}>
                        {floorName || `Floor ${floorId}`}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Flat/Area
                </label>
                <input
                  type="text"
                  placeholder="Text input"
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm"
                  value={formData.location_flat_area}
                  onChange={(e) =>
                    handleChange("location_flat_area", e.target.value)
                  }
                  required
                />
              </div>
            </div>
            <div className="hidden">
              <label className="text-xs text-slate-500 block mb-1">
                Combined Location
              </label>
              <input
                type="text"
                placeholder="Combined textbox"
                className="w-full rounded-lg border border-slate-300 p-2.5 text-sm bg-slate-100"
                value={formData.location_combined}
                onChange={(e) =>
                  handleChange("location_combined", e.target.value)
                }
                required
                readOnly
              />
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-start gap-3">
              <QuestionBadge number={3} />
              <div>
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  PHOTOGRAPH OF UNSAFE ACT / CONDITION{" "}
                  <span className="text-red-500">*</span>
                </p>
              </div>
            </div>
            {observationPhotos.length === 0 ? (
              <div className="w-full rounded-xl border-2 border-dashed border-orange-200 bg-white px-4 py-8 sm:py-10 transition-colors hover:bg-orange-50/50">
                <div className="flex flex-col items-center justify-center text-center">
                  <UploadCloud className="mb-4 h-10 w-10 text-orange-400" />

                  <p className="mb-2 text-sm font-semibold text-slate-700">
                    Attach observation photo
                  </p>

                  <p className="mb-5 text-xs text-slate-400">
                    PNG, JPG, JPEG (Select single or multiple photos)
                  </p>

                  <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                    {/* Gallery / File Upload */}
                    <label
                      htmlFor="unsafe-act-gallery-upload"
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm font-semibold text-orange-700 hover:bg-orange-100 transition-colors"
                    >
                      <UploadCloud className="h-4 w-4" />
                      Select from Gallery
                    </label>

                    {/* Camera Capture */}
                    <button
                      type="button"
                      onClick={() => setCameraOpen(true)}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 transition-colors"
                    >
                      <Camera className="h-4 w-4" />
                      Take Photo
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Header with Photo count and Add More actions */}
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orange-100 bg-orange-50/60 p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800">
                      Uploaded Photos ({observationPhotos.length})
                    </span>
                    <span className="text-xs text-slate-500 hidden sm:inline">
                      — You can annotate or comment each photo below
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="unsafe-act-gallery-upload"
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100 shadow-sm transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add More from Gallery
                    </label>
                    <button
                      type="button"
                      onClick={() => setCameraOpen(true)}
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 shadow-sm transition-colors"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      Take Photo
                    </button>
                  </div>
                </div>

                {/* List of uploaded photo cards */}
                <div className="space-y-3">
                  {observationPhotos.map((photo, index) => (
                    <div
                      key={photo.id || index}
                      className="rounded-xl border border-orange-200 bg-white p-3.5 shadow-sm transition-all hover:border-orange-300 hover:shadow"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className="relative h-12 w-12 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-slate-200 bg-slate-100 group"
                            onClick={() => {
                              if (photo.previewUrl) window.open(photo.previewUrl, "_blank");
                            }}
                            title="Click to view full size"
                          >
                            <img
                              src={photo.previewUrl}
                              alt={`Photo ${index + 1}`}
                              className="h-full w-full object-cover transition-transform group-hover:scale-105"
                            />
                            <span className="absolute bottom-0 right-0 rounded-tl bg-slate-900/80 px-1 py-0.5 text-[9px] font-bold text-white leading-none">
                              #{index + 1}
                            </span>
                          </div>

                          <div className="min-w-0 max-w-[170px] xs:max-w-[220px] sm:max-w-md">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-medium text-slate-800">
                                {photo.file?.name || `Photo ${index + 1}`}
                              </p>
                              {photo.annotatedFile && (
                                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200">
                                  Annotated
                                </span>
                              )}
                            </div>

                            <p className="text-xs text-slate-500">
                              {photo.file?.size
                                ? `${(photo.file.size / 1024 / 1024).toFixed(2)} MB`
                                : ""}
                            </p>
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setAnnotatingPhotoIndex(index)}
                            className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            Annotate
                          </button>

                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="mt-2.5">
                        <input
                          type="text"
                          className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                          placeholder={`Add comments about photo #${index + 1}...`}
                          value={photo.comment}
                          onChange={(e) =>
                            handleUpdatePhotoComment(index, e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hidden file inputs: gallery supports multiple */}
            <input
              id="unsafe-act-gallery-upload"
              type="file"
              multiple
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                handleAddPhotos(e.target.files, true);
                e.target.value = "";
              }}
            />

            <input
              id="unsafe-act-camera-upload"
              type="file"
              className="hidden"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                handleAddPhotos(e.target.files, true);
                e.target.value = "";
              }}
            />

            <SafetyImageAnnotationModal
              open={annotatingPhotoIndex !== null}
              file={
                annotatingPhotoIndex !== null && observationPhotos[annotatingPhotoIndex]
                  ? observationPhotos[annotatingPhotoIndex].annotatedFile ||
                    observationPhotos[annotatingPhotoIndex].file
                  : null
              }
              onClose={() => setAnnotatingPhotoIndex(null)}
              onSave={handleSaveAnnotation}
            />

            <SafetyCameraCaptureModal
              open={cameraOpen}
              onClose={() => setCameraOpen(false)}
              onCapture={(capturedFile) => handleAddPhotos([capturedFile], true)}
              title="Take Observation Photo"
            />
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex flex-wrap items-start gap-3">
              <QuestionBadge number={4} />
              <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  HAZARD/RISK <span className="text-red-500">*</span>
                </p>
                <button
                  type="button"
                  onClick={() => setShowRiskMatrix(true)}
                  className="text-xs font-bold bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                >
                  View Risk Matrix
                </button>
              </div>
            </div>
            <label className="text-xs text-slate-500 block mb-2">
              Hazard (Checkboxes)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {HAZARDS_OPTIONS.map((opt) => (
                <label key={opt} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="shrink-0 rounded border border-slate-300 text-orange-600 focus:ring-orange-500"
                    checked={formData.hazard_categories.includes(opt)}
                    onChange={() => handleHazardToggle(opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
            {formData.hazard_categories.includes("10. Other Hazards") && (
              <div className="mb-4">
                <label className="text-xs text-slate-500 block mb-1">
                  Please specify Other Hazards{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter other hazard details..."
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm"
                  value={otherHazardText}
                  onChange={(e) => setOtherHazardText(e.target.value)}
                  required
                />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Risk</label>
              <select
                className={`w-full rounded-lg border p-2.5 text-sm focus:ring-2 focus:ring-offset-1 focus:outline-none ${
                  formData.risk === "Low Risk"
                    ? "border-green-500 bg-green-50 text-green-700 focus:ring-green-500"
                    : formData.risk === "Medium Risk"
                      ? "border-yellow-500 bg-yellow-50 text-yellow-700 focus:ring-yellow-500"
                      : formData.risk === "High Risk"
                        ? "border-red-500 bg-red-50 text-red-700 focus:ring-red-500"
                        : "border-slate-300 focus:ring-orange-500 bg-white text-slate-700"
                }`}
                value={formData.risk}
                                onChange={(e) => {
                  const val = e.target.value;
                  handleChange("risk", val);
                  if (
                    val === "Medium Risk" ||
                    val === "High Risk" ||
                    val === "Low Risk"
                  ) {
                    const now = new Date();
                    if (val === "Medium Risk") {
                      now.setHours(now.getHours() + 24);
                    } else if (val === "High Risk") {
                      now.setHours(now.getHours() + 4);
                    }
                    // Low Risk: no offset — just prefill current date/time
                    const pad = (num) => num.toString().padStart(2, "0");
                    const newDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                    handleChange("target_date", newDate);
                  } else if (val === "") {
                    handleChange("target_date", "");
                  }
                }}
                required
              >
                <option value="">Select Risk Level...</option>
                <option value="Low Risk">Low Risk</option>
                <option value="Medium Risk">Medium Risk</option>
                <option value="High Risk">High Risk</option>
              </select>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-start gap-3">
              <QuestionBadge number={5} />
              <div>
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  CONTRACTOR
                </p>
              </div>
            </div>

            <select
              className="w-full sm:max-w-sm rounded-lg border border-slate-300 p-2.5 text-sm disabled:cursor-not-allowed disabled:bg-slate-100"
              value={
                formData.name_of_contractor ||
                (defaultInternalContractor
                  ? defaultInternalContractor.contractorName
                  : "")
              }
              onChange={(event) => {
                const val = event.target.value;
                const matchedOption = uniqueContractorOptions.find(
                  (opt) =>
                    opt.contractorName === val ||
                    opt.normalizedName === val.toLowerCase(),
                );

                let candidateContractorId = "";
                if (matchedOption?.contractorId) {
                  candidateContractorId = String(matchedOption.contractorId);
                } else if (
                  defaultInternalContractor &&
                  (val === defaultInternalContractor.contractorName || val === "")
                ) {
                  candidateContractorId = defaultInternalContractor.contractorId
                    ? String(defaultInternalContractor.contractorId)
                    : "";
                }

                setFormData((prev) => ({
                  ...prev,
                  name_of_contractor: val,
                  contractor_id: candidateContractorId,
                }));
              }}
              disabled={loadingContractors}
            >
              {defaultInternalContractor ? (
                <option
                  key={
                    defaultInternalContractor.contractorId
                      ? `contractor-${defaultInternalContractor.contractorId}`
                      : "contractor-internal-default"
                  }
                  value={defaultInternalContractor.contractorName}
                >
                  {defaultInternalContractor.contractorName}
                </option>
              ) : (
                <option value="">{internalTeamLabel}</option>
              )}

              {uniqueContractorOptions
                .filter((contractorOption) => {
                  const matchesDefault =
                    defaultInternalContractor &&
                    contractorOption.normalizedName ===
                      defaultInternalContractor.normalizedName;
                  const matchesInternalLabel =
                    internalTeamLabel &&
                    contractorOption.normalizedName ===
                      internalTeamLabel.toLowerCase().trim();
                  return !matchesDefault && !matchesInternalLabel;
                })
                .map((contractorOption) => (
                  <option
                    key={
                      contractorOption.contractorId
                        ? `contractor-${contractorOption.contractorId}`
                        : `contractor-${contractorOption.normalizedName}`
                    }
                    value={contractorOption.contractorName}
                  >
                    {contractorOption.contractorName}
                  </option>
                ))}
            </select>

            {loadingContractors && (
              <p className="mt-2 text-xs text-slate-500">
                Loading contractors...
              </p>
            )}
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-start gap-3">
              <QuestionBadge number={6} />
              <div>
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  TARGET DATE <span className="text-red-500">*</span>
                </p>
              </div>
            </div>
            <div className="flex flex-col xs:flex-row flex-wrap items-stretch xs:items-center gap-4">
              <div className="w-full xs:w-auto">
                <label className="text-xs font-medium text-slate-500 block mb-1">
                  Date
                </label>
                <input
                  type="date"
                  className={`w-full xs:w-[160px] rounded-lg border p-2.5 text-sm focus:ring-2 focus:ring-offset-1 focus:outline-none ${
                    formData.risk === "Low Risk"
                      ? "border-green-500 bg-green-50 text-green-700 focus:ring-green-500"
                      : formData.risk === "Medium Risk"
                        ? "border-yellow-500 bg-yellow-50 text-yellow-700 focus:ring-yellow-500"
                        : formData.risk === "High Risk"
                          ? "border-red-500 bg-red-50 text-red-700 focus:ring-red-500"
                          : "border-slate-300 focus:ring-orange-500 bg-white text-slate-700"
                  }`}
                  value={(formData.target_date || "").split("T")[0] || ""}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const timePart =
                      (formData.target_date || "").split("T")[1] || "";
                    handleChange(
                      "target_date",
                      newDate ? `${newDate}T${timePart}` : "",
                    );
                  }}
                  required
                />
              </div>
              <div className="w-full xs:w-auto">
                <label className="text-xs font-medium text-slate-500 block mb-1">
                  Time (hh:mm)
                </label>
                <input
                  type="time"
                  className={`w-full xs:w-[130px] rounded-lg border p-2.5 text-sm focus:ring-2 focus:ring-offset-1 focus:outline-none ${
                    formData.risk === "Low Risk"
                      ? "border-green-500 bg-green-50 text-green-700 focus:ring-green-500"
                      : formData.risk === "Medium Risk"
                        ? "border-yellow-500 bg-yellow-50 text-yellow-700 focus:ring-yellow-500"
                        : formData.risk === "High Risk"
                          ? "border-red-500 bg-red-50 text-red-700 focus:ring-red-500"
                          : "border-slate-300 focus:ring-orange-500 bg-white text-slate-700"
                  }`}
                  value={(formData.target_date || "").split("T")[1] || ""}
                  onChange={(e) => {
                    const newTime = e.target.value;
                    const datePart =
                      (formData.target_date || "").split("T")[0] ||
                      new Date().toISOString().split("T")[0];
                    handleChange("target_date", `${datePart}T${newTime}`);
                  }}
                  required
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md">
            <div className="mb-4 flex items-start gap-3">
              <QuestionBadge number={7} />
              <div>
                <p className="pt-1 text-sm font-semibold text-slate-800 sm:text-base">
                  CA/PA TO BE TAKEN <span className="text-red-500">*</span>
                </p>
              </div>
            </div>
            <div className="space-y-4">
                           <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Corrective Action
                </label>
                <NumberedTextArea
                  name="corrective_action"
                  placeholder="Enter CA..."
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm"
                  rows={3}
                  value={formData.corrective_action}
                  onChange={(e) =>
                    handleChange("corrective_action", e.target.value)
                  }
                  required
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">
                  Preventive Action
                </label>
                <NumberedTextArea
                  name="preventive_action"
                  placeholder="Enter PA..."
                  className="w-full rounded-lg border border-slate-300 p-2.5 text-sm"
                  rows={3}
                  value={formData.preventive_action}
                  onChange={(e) =>
                    handleChange("preventive_action", e.target.value)
                  }
                  required
                />
              </div>
              <div className="hidden">
                <label className="text-xs text-slate-500 block mb-1">
                  Final Combined
                </label>
                <div
                  className="w-full rounded-lg border border-slate-300 p-3 text-sm bg-slate-100 whitespace-pre-wrap min-h-[5rem]"
                  dangerouslySetInnerHTML={{
                    __html:
                      formData.ca_pa_combined ||
                      "<span class='text-slate-400'>Final textbox will show these two inputs along with these two texts</span>",
                  }}
                />
              </div>
            </div>
          </div>


          <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="w-full sm:w-auto rounded-lg border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {submitting ? "Submitting..." : "Submit Observation"}
            </button>
          </div>
        </form>
      </div>

      {isSignatureModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md animate-in zoom-in-95 rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Final Signature
              </h3>
              <button
                type="button"
                onClick={() => {
                  sigPadRef.current?.clear();
                  setIsSignatureModalOpen(false);
                }}
                className="rounded p-1 text-muted-foreground hover:bg-muted"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Draw your signature and submit the observation.
            </p>
            <div className="rounded-lg border border-border bg-white p-2">
              <SafetyResponsiveSignatureCanvas ref={sigPadRef} penColor="black" />
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => sigPadRef.current?.clear()}
                className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  if (sigPadRef.current?.isEmpty()) {
                    showToast("Please provide your signature.", "error");
                    return;
                  }
                  const sigData = sigPadRef.current
                    .getCanvas()
                    .toDataURL("image/png");
                  fetch(sigData)
                    .then((res) => res.blob())
                    .then((blob) => {
                      const file = new File(
                        [blob],
                        `creator-signature-${Date.now()}.png`,
                        { type: "image/png" },
                      );
                      setIsSignatureModalOpen(false);
                      confirmSubmit(file);
                    });
                }}
                className="w-full rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 py-2.5 text-xs font-semibold text-white shadow-sm hover:from-orange-500 hover:to-orange-600 disabled:opacity-60 transition-all"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Observation"}
              </button>
            </div>
          </div>
        </div>
      )}
      <RiskMatrixModal
        open={showRiskMatrix}
        onClose={() => setShowRiskMatrix(false)}
      />
      {submitting && locationVerificationStatus && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl bg-slate-900/95 text-white px-5 py-3 shadow-2xl backdrop-blur-md border border-slate-700/50">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-orange-400 border-t-transparent" />
          <span className="text-sm font-medium">{locationVerificationStatus}</span>
        </div>
      )}
    </div>
  );
}